import { DataGrid } from '@material-ui/data-grid';
import { useState, useContext, useEffect, useCallback, createRef } from 'react';
import { InterfaceContext } from '../model/InterfaceProvider';
import * as LQ from '../controller/LayerQueries';
import TableGridColumns from '../model/TableColumns';
import { GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarContainer } from '@material-ui/data-grid';
import { getCSVData } from '../controller/getCSVData';
import { tableDataMapping } from '../model/DataTransformation';
import TableFilterBar from './TableFilterBar';

const TableGrid = () => {
    const [rows, setRows] = useState([]);
    const [columns, setcolumns] = useState([]);
    const [columnsFbar, setcolumnsFbar] = useState([]);
    const { scope, tract, theme, usState, year, tab, setTableData, setFipsInView, highlightedFips, setErrorMessage } = useContext(InterfaceContext);
    const wrapDiv = createRef();
    const [loading, setLoading] = useState(true);
    const { showDownloadModal } = useContext(InterfaceContext);
    const [filtersOn, setFilterOn] = useState(false);
    const [allRows, setAllRows] = useState(() => []); // for restoring after search
    const tableToolbar = () => {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
            </GridToolbarContainer>
        );
    };
    const dataToTable = useCallback(
        (data) => {
            // ΔVΔVΔ: Map table data input with local column data
            const TableColumns = TableGridColumns[year.columns];
            const TableGridColumnKeys = Object.keys(TableColumns).map((key) => key.toLowerCase());
            const cols = Object.keys(data[0])
                .reduce((output, col, i) => {
                    const slug = col.toLowerCase();
                    var order = TableGridColumnKeys.indexOf(slug);
                    if (order === -1) return output;
                    const columnProps = TableColumns[col] || TableColumns[slug] || TableColumns[col.toUpperCase()]; // || TableColumns[headerName];
                    const adjCol = Object.assign({ field: col, width: 200, minWidth: 50, cellClassName: columnProps?.class, headerClassName: columnProps?.class }, columnProps, { order });
                    output.push(adjCol);
                    return output;
                }, [])
                .sort((a, b) => {
                    return a.order - b.order;
                });
            setcolumns(cols);
            const filteredColumns = filteredColumnsForTabFilterBar(cols, usState?.label);
            setcolumnsFbar(filteredColumns);
            if (highlightedFips) {
                let findHighlightInd = data.findIndex((d) => d.FIPS === highlightedFips + '');
                if (findHighlightInd > 0) {
                    if (findHighlightInd) {
                        let a, b, c;
                        a = data.slice(0, findHighlightInd);
                        b = data.slice(findHighlightInd, findHighlightInd + 1);
                        c = data.slice(findHighlightInd + 1);
                        data = [...b, ...a, ...c];
                    }
                }
            }
            setRows(data);
            setAllRows(data);
        },
        [LQ, scope, theme, tract, usState, year]
    );

    const openDocumentation = () => {
        window.open(year.docs + '#page=' + year.varpage);
    };

    const getData = async () => {
        if (scope === 'state' && !usState) return;
        setLoading(true);
        const csvdata = await getCSVData({ yearLabel: year.label, usStateLabel: usState.label, scopeSlug: scope, tractSlug: tract });
        dataToTable(csvdata);
        document.querySelector('.MuiDataGrid-window')?.setAttribute('tabindex', 0);
        setLoading(false);
    };
    const toggleFilters = () => {
        setFilterOn(!filtersOn);
    };
    const filtersUpdated = (conditions) => {
        var newRows = [...allRows];
        if (conditions.length) {
            for (let i = 0; i < conditions.length; i++) {
                const row = conditions[i];
                if (row.andOr.toLowerCase() === 'and') {
                    newRows = filterTableGridField(row.value, row.field, row.matchCondition, newRows);
                } else {
                    const conditionResults = filterTableGridField(row.value, row.field, row.matchCondition, allRows);
                    const filtered = conditionResults.filter((row) => {
                        return allRows.find((item) => item.id === row.id);
                    });
                    newRows = [...newRows, ...filtered];
                }
            }
        }
        // console.log('AR length', allRows.length);
        // console.log('NR length', newRows.length);
        setRows(newRows);
    };
    const filterTableGridField = (value, field, matchCondition, data, autosort = false) => {
        const srch = String(value).toLowerCase();
        const numSrch = parseInt(value);
        const filtered = data.filter((row) => {
            var strField = String(row[field]).toLowerCase();
            var numField = parseInt(row[field]);
            switch (matchCondition) {
                case 'contains':
                    return strField.indexOf(srch) !== -1;
                case '=':
                    return strField === srch;
                case '!=':
                    return strField !== srch;
                case 'isEmpty':
                    return strField === '';
                case 'isNotEmpty':
                    return strField !== '';
                case '>':
                    return numField > numSrch;
                case '>=':
                    return numField >= numSrch;
                case '<':
                    return numField < numSrch;
                case '<=':
                    return numField <= numSrch;
            }
        });
        return filtered;
    };

    useEffect(() => {
        setTableData(rows);
    }, [rows]);

    useEffect(() => {
        if (tab.slug !== 'data') return; //don't fetch data if table is nt being viewed
        if (!tract) return;
        getData();
    }, [scope, theme, tract, usState, year, tab]);

    const filteredColumnsForTabFilterBar = (columns, state = false) => {
        const blacklist = ['releaseyear', 'year', 'comparison', 'geolevel'];
        const stateBlackList = ['st', 'state_name', 'state_abbr'];
        return columns.filter((col) => {
            const field = col.field.toLowerCase();
            if (state && stateBlackList.indexOf(field) != -1) return false;
            return blacklist.indexOf(field) == -1;
        });
    };

    return (
        <div ref={wrapDiv} id="data-table" className={loading ? 'is_loading' : ''} style={{ height: 250, width: '50%' }}>
            <div className={'download-grid'}>
                <button
                    onClick={() => {
                        openDocumentation();
                    }}
                >
                    View Data Dictionary
                </button>
                <button
                    onClick={() => {
                        showDownloadModal();
                    }}
                >
                    Download
                </button>
            </div>
            <div className="filter-panel">
                <button className="adv-search-btn" onClick={toggleFilters}>
                    {filtersOn ? 'Hide' : 'Show'} Advanced Search
                </button>
                <TableFilterBar columns={columnsFbar} visible={filtersOn} onChange={filtersUpdated} />
            </div>
            <DataGrid getRowClassName={(params) => `${params.row.FIPS === highlightedFips ? 'highlight' : ''}`} columns={columns} rows={rows} loading={loading} components={{ Toolbar: tableToolbar }} />
        </div>
    );
};

export default TableGrid;
